/**
 * PatientNameTag - inline name + tokenized `[SYNTHETIC]` suffix (PROD-569).
 *
 * Mitigation 2 of the ADR-PROTO-002 4-mitigation stack: every appearance
 * of a patient OR clinician name in clinician-facing UI carries a
 * tokenized `[SYNTHETIC]` suffix. The component accepts a plain `name`
 * string (e.g. `Khalid Al-Mansoori` or `Dr. Al-Dhaheri`) and renders
 * `{name} [SYNTHETIC]` with the suffix as a Signal DS-tokenized inline
 * tag, NOT a free-text string.
 *
 * Signal DS sourcing per COMPONENT-CONTRACTS §5 (PatientNameTag):
 *   - Inline tag: Component (Tag-inline variant `synthetic`)
 *     [GAP: signal-ds-package] - tracked as G6 in
 *     docs/SIGNAL-DS-CONTRIBUTIONS.md.
 *   - Tag color token: Token color (`warning-soft`) - DS-existing via
 *     src/styles/tokens.ts (G6 also covers the token contribution).
 *
 * HC2: every color resolves through src/styles/tokens.ts (no raw hex).
 * HC5: zero em dashes; the suffix is the literal four-character string
 * `[SYNTHETIC]` rendered inside a tokenized span.
 * HC6 a11y: the tag is intentionally `aria-hidden="true"`. The chart-
 * header `<SyntheticBadge>` is the canonical screen-reader announcement
 * (`role="status" aria-live="polite"`); reading `[SYNTHETIC]` after every
 * patient + clinician name in a list of four would be punishing for SR
 * users. The visual marker remains for sighted users; the badge announces
 * the safety context.
 *
 * Verification: tests/patient-name-tag.test.tsx covers PNT-1 through
 * PNT-4 (rendering, clinician name shape, no free-text suffix, compact
 * variant contrast).
 */
export interface PatientNameTagProps {
  /**
   * The patient or clinician name to render verbatim before the
   * `[SYNTHETIC]` tag. Accepts patient names (`Khalid Al-Mansoori`) or
   * clinician names (`Dr. Al-Dhaheri`). Trimmed at the call site; this
   * component does not normalise.
   */
  name: string;
  /**
   * Visual variant per COMPONENT-CONTRACTS §5 PNT-4. Defaults to
   * `default`. `clinician` adds the bold weight expected on the byline
   * surface; `compact` is print-safe (heavier weight + AA contrast).
   */
  variant?: 'default' | 'clinician' | 'compact';
  className?: string;
}

const NAME_VARIANT_CLASSES: Record<
  NonNullable<PatientNameTagProps['variant']>,
  string
> = {
  default: 'font-semibold text-text-primary',
  clinician: 'font-bold text-text-primary',
  compact: 'font-bold text-text-primary',
};

const TAG_VARIANT_CLASSES: Record<
  NonNullable<PatientNameTagProps['variant']>,
  string
> = {
  default:
    'ml-gutter-sm rounded-signal-sm bg-warning-soft-bg px-gutter-sm py-0.5 text-signal-xs font-semibold uppercase tracking-wide text-warning-soft-fg ring-1 ring-inset ring-warning-soft-border',
  clinician:
    'ml-gutter-sm rounded-signal-sm bg-warning-soft-bg px-gutter-sm py-0.5 text-signal-xs font-semibold uppercase tracking-wide text-warning-soft-fg ring-1 ring-inset ring-warning-soft-border',
  // Compact / print-safe: heavier ring + bg for AA contrast.
  compact:
    'ml-1 rounded-signal-sm bg-warning-strong-bg px-1.5 py-0 text-signal-xs font-bold uppercase tracking-wider text-warning-strong-fg ring-1 ring-inset ring-warning-strong-border',
};

/** Verbatim suffix string. HC5: hyphen-only universe; this is bracket text. */
export const PATIENT_NAME_TAG_SUFFIX = '[SYNTHETIC]';

export function PatientNameTag({ name, variant = 'default', className }: PatientNameTagProps) {
  const wrapperClasses = [NAME_VARIANT_CLASSES[variant], 'inline-flex items-baseline', className ?? '']
    .filter(Boolean)
    .join(' ');

  return (
    <span
      data-patient-name-tag={variant}
      data-name={name}
      className={wrapperClasses}
    >
      <span data-name-text>{name}</span>
      <span
        aria-hidden="true"
        data-synthetic-suffix="canonical"
        className={TAG_VARIANT_CLASSES[variant]}
      >
        {PATIENT_NAME_TAG_SUFFIX}
      </span>
    </span>
  );
}
